import React, { useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Row, Button } from "antd"
import Title from "antd/es/typography/Title"
import { Container } from "react-bootstrap"
import Navbar from "../../components/Navbar/Navbar"
import NewLogo from "../../assets/images/logo.png"
import {
  capitalizeEachWord,
  formatAmount,
  formatDate,
  removeUnderscoreToSentence,
} from "../../utils/Common"
import ReactToPrint from "react-to-print"
import { PrinterOutlined } from "@ant-design/icons"
import { UsePostRequest } from "../../utils/api/ApiRequests"

export default function ViewLoan() {
  const componentRef = useRef()
  const navigate = useNavigate()
  const location = useLocation()
  const passedData = location.state
  const dateToday = new Date()
  const [paymentSummary, setPaymentSummary] = useState([])
  const [paymentHistory, setPaymentHistory] = useState([])

  React.useEffect(() => {
    async function fetchLoanData() {
      setPaymentHistory([])
      setPaymentSummary([])
      const response = await UsePostRequest("employee_loans/get_loan_history", {
        loan_id: passedData.id,
      })
      if (response.response) {
        setPaymentSummary(response.response.employee_history.overview)
        setPaymentHistory(response.response.employee_history.loan_history)
      }
    }

    fetchLoanData()
  }, [passedData])

  return (
    <Navbar>
      <Container className="print-report-page p-3 view-page">
        <div ref={componentRef}>
          <Row className="d-flex justify-content-end">
            <Title level={3}>LOAN</Title>
          </Row>
          <Row className="d-flex justify-content-end">
            Date Printed: {formatDate(dateToday)}
          </Row>
          <Row className="px-1 ms-2">
            <img src={NewLogo} alt={"logo"} className="payslip-logo" />
          </Row>
          <Row className="bold p-1">{window.$company_name}</Row>
          <Row className="p-1">{window.$company_address}</Row>

          {/* ── Loan Information ── */}
          <Row className="p-1 mt-3">
            <table className="table table-bordered">
              <thead>
                <tr className="primary-bg white-text">
                  <th colSpan={2}>Loan Information</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="bold w-50">Employee Name</td>
                  <td>
                    {passedData.employee_name
                      ? capitalizeEachWord(passedData.employee_name)
                      : ""}
                  </td>
                </tr>
                <tr>
                  <td className="bold w-50">Request Date</td>
                  <td>{formatDate(passedData.issue_date)}</td>
                </tr>
                <tr>
                  <td className="bold w-50">Loan Type</td>
                  <td>{removeUnderscoreToSentence(passedData.loan_type)}</td>
                </tr>
                <tr>
                  <td className="bold w-50">Area/Dept</td>
                  <td>{passedData.area_dept || ""}</td>
                </tr>
                <tr>
                  <td className="bold w-50">Purpose</td>
                  <td>{passedData.reason || ""}</td>
                </tr>
                <tr>
                  <td className="bold w-50">Date Needed</td>
                  <td>{formatDate(passedData.date_needed)}</td>
                </tr>
                <tr>
                  <td className="bold w-50">Total Approved Amount</td>
                  <td>{formatAmount(passedData.installment)}</td>
                </tr>
                <tr>
                  <td className="bold w-50">Deduction per Payday</td>
                  <td>{formatAmount(passedData.charge_per_installment)}</td>
                </tr>
                <tr>
                  <td className="bold w-50">First Deduction Date</td>
                  <td>{formatDate(passedData.first_deduction_date)}</td>
                </tr>
                <tr>
                  <td className="bold w-50">Estimated Last Deduction Date</td>
                  <td>
                    {formatDate(passedData.estimated_last_deduction_date)}
                  </td>
                </tr>
                <tr>
                  <td className="bold w-50">Term</td>
                  <td>{removeUnderscoreToSentence(passedData.term)}</td>
                </tr>
              </tbody>
            </table>
          </Row>

          {/* ── Payment Summary ── */}
          <Row className="mt-4">
            <Row className="py-2">
              <Title level={5}>PAYMENT SUMMARY</Title>
            </Row>
            <table className="table table-bordered">
              <thead>
                <tr className="primary-bg white-text">
                  <th className="text-end">Loan Amount</th>
                  <th className="text-end">Paid Amount</th>
                  <th className="text-end">Remaining Balance</th>
                </tr>
              </thead>
              <tbody>
                {paymentSummary &&
                  paymentSummary.map((data) => (
                    <tr>
                      <td className="text-end">
                        Php {formatAmount(data.loan_amount)}
                      </td>
                      <td className="text-end">
                        Php {formatAmount(data.paid_amount)}
                      </td>
                      <td className="text-end">
                        Php {formatAmount(data.remaining_balance)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Row>

          {/* ── Payment History ── */}
          <Row className="mt-3">
            <Row className="py-2">
              <Title level={5}>PAYMENT HISTORY</Title>
            </Row>
            <table className="table table-bordered cont-history-wrapper">
              <thead>
                <tr className="primary-bg white-text">
                  <th>Date</th>
                  <th className="text-end">Paid Amount</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory && paymentHistory.length
                  ? paymentHistory.map((data) => {
                      return (
                        <tr>
                          <td>
                            {data.date
                              ? formatDate(data.date) ??
                                `${data.month} ${data.year}`
                              : ""}
                          </td>
                          <td className="text-end">
                            Php {formatAmount(data.loan_payment)}
                          </td>
                          <td>{data.remarks}</td>
                        </tr>
                      )
                    })
                  : ""}
              </tbody>
            </table>
          </Row>

          {/* ── Action Buttons ── */}
          <Row className="d-flex justify-content-end my-4 noPrint">
            <Row>
              <Button className="me-2" onClick={() => navigate(-1)}>
                Back
              </Button>
              <ReactToPrint
                trigger={() => (
                  <Button type="primary">
                    <PrinterOutlined />
                    Print
                  </Button>
                )}
                content={() => componentRef.current}
                documentTitle={"Loan"}
              />
            </Row>
          </Row>
        </div>
      </Container>
    </Navbar>
  )
}
